import * as Collapsible from '@radix-ui/react-collapsible';
import { ProductionUnit, ProjectArrayDropdownOptions } from '~/constants';
import { ProjectArray } from '~/types/project';
import { WorksheetField } from '../../../../worksheets/common/components/Field';
import { InverterTypeDropdownOptions, ModuleOrientationDropdownOptions, ModuleTypeDropdownOptions } from '~/constants/production-inputs';
import { useFadeInStyles, useProjectContext } from '~/hooks';
import ProjectArraySectionHeader from './ProjectArraySectionHeader';
import { useEffect, useState } from 'react';
import WorksheetForm from '~/features/projectWizard/worksheets/common/components/WorksheetForm';
import Button from '~/components/Button';
import Modal from '~/components/Modal';
import './styles.scss';

export default function ProjectArraySection({ projectArray }: { projectArray: ProjectArray }) {
  const { project, projectState, updateProjectValue } = useProjectContext();

  const projectArrays = project.projectArrays ?? [];
  const [open, setOpen] = useState(projectArrays.length > 1 ? false : true);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    // New arrays should always start expanded
    if (projectArray.startOpen) {
      setOpen(true);
      delete projectArray.startOpen;
    }
  }, []);

  const removeArray = () => {
    const newArrays = projectArrays
      .filter((array) => array.order !== projectArray.order)
      .map((array, index) => ({ ...array, order: index }));

    updateProjectValue('projectArrays', newArrays);
    setShowRemoveConfirm(false);
  };

  // Returns a schemaKey like projectArrays.0.type for saving the value in the project object
  const schemaKey = (term: string) => `projectArrays.${projectArray.order}.${term}`;

  return (
    <section className="collapsible-card collapsible-card--project-array" style={useFadeInStyles()}>
      <Collapsible.Root open={open} onOpenChange={() => setOpen(!open)}>
        <ProjectArraySectionHeader
          projectArray={projectArray}
          isCollapsed={!open}
          onRemove={() => setShowRemoveConfirm(true)}
        />
        <Collapsible.Content className="CollapsibleContent">
          <WorksheetForm isInner>
            <WorksheetField.Row>
              <WorksheetField
                type='dropdown'
                schemaKey={schemaKey('type')}
                placeholder="Select an array type"
                options={ProjectArrayDropdownOptions}
                fieldLabel='Array type'
              />
            </WorksheetField.Row>
            {/* Only show roofCondition field for ROOFTOP type arrays */}
            {projectArray.type === 'ROOFTOP' && (
              <WorksheetField.Row>
                <WorksheetField
                  schemaKey={schemaKey('roofCondition')}
                  placeholder="Enter roof condition"
                  fieldLabel='Roof condition'
                />
              </WorksheetField.Row>
            )}
            <WorksheetField.Row>
              <WorksheetField
                type="number"
                schemaKey={schemaKey('sizeKwdc')}
                fieldLabel="Size in kWdc"
                fieldLabelStyle={{ marginBottom: projectState?.isEpcOperating ? '1.25rem' : '0.28571429rem' }}
                placeholder="Enter size"
                label="kWdc"
                width="half"
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <div className="radio-selections">
                <WorksheetField
                  type="number"
                  fieldLabel={projectState?.isEpcOperating ? 'Annual production' : 'Year one production'}
                  helpText={projectState?.isEpcOperating  ? 'Expected next 12 months production' : ''}
                  schemaKey={schemaKey('yearOneProduction')}
                  decimals={0}
                  unitField="yearOneProductionUnit"
                  placeholder="Enter production"
                  units={[
                    { value: ProductionUnit.KWH, label: 'kWh' },
                    { value: ProductionUnit.KWHKW, label: 'kWh/kW' },
                  ]}
                  defaultUnit={ProductionUnit.KWHKW}
                  width="half"
                />
              </div>
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                type="number"
                schemaKey={schemaKey('sizeKwac')}
                fieldLabel="Size in kWac"
                placeholder="Enter size"
                label="kWac"
                optional={project.addressState !== 'Illinois'}
                width="half"
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                type="dropdown"
                schemaKey={schemaKey('moduleType')}
                placeholder="Select module type"
                options={ModuleTypeDropdownOptions}
                fieldLabel="Module type"
                optional
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <div className="isBifacial">
                <WorksheetField
                  type="radio"
                  schemaKey={schemaKey('isBifacial')}
                  fieldLabel="Bifacial modules?"
                  values={[
                    { value: true, label: 'Yes' },
                    { value: false, label: 'No' },
                  ]}
                  inline
                  optional
                />
              </div>
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                type="dropdown"
                schemaKey={schemaKey('inverterType')}
                placeholder="Select inverter type"
                options={InverterTypeDropdownOptions}
                fieldLabel='Inverter type'
                optional
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                type="number"
                schemaKey={schemaKey('derateFactor')}
                fieldLabel="Shading loss / derate factor"
                placeholder="Enter shading loss"
                label="%"
                optional
                helpText="Enter horizon and near shading; not inter-row shading."
                width="half"
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                type="dropdown"
                schemaKey={schemaKey('moduleOrientation')}
                placeholder="Select orientation"
                options={ModuleOrientationDropdownOptions}
                fieldLabel='Module orientation'
                optional
              />
            </WorksheetField.Row>
            <WorksheetField.Row>
              <WorksheetField
                type="number"
                schemaKey={schemaKey('moduleTiltDegrees')}
                fieldLabel="Module tilt"
                placeholder="Enter tilt"
                label="degrees"
                optional
                helpText="0 - 90° (for trackers, max rotation angle)"
                width="half"
              />
            </WorksheetField.Row>
            <WorksheetField.Row style={{ marginBottom: '1rem' }}>
              <WorksheetField
                type="number"
                schemaKey={schemaKey('azimuthDegrees')}
                fieldLabel="Azimuth"
                placeholder="Enter azimuth"
                label="degrees"
                optional
                helpText="North = 0°; East = 90°; South = 180°; West = 270° "
                width="half"
              />
            </WorksheetField.Row>
          </WorksheetForm>
        </Collapsible.Content>
      </Collapsible.Root>
      {showRemoveConfirm && (
        <Modal closeModal={() => setShowRemoveConfirm(false)}>
          <Modal.Title>Remove array {projectArray.order + 1}?</Modal.Title>
          <Modal.Content>
            <p>
              This will permanently remove this array and all of its inputs from the project.
              This action cannot be undone.
            </p>
          </Modal.Content>
          <Modal.Footer>
            <Button primary outlined onClick={() => setShowRemoveConfirm(false)}>
              Cancel
            </Button>
            <Button primary onClick={removeArray}>
              Remove array
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </section>
  );
}
